<script setup>
/**
 * What the customer's phone will show for one step.
 *
 * IT IS `MenuPreview.vue`, NOT A SECOND MOCK. WhatsApp's rules — three options or fewer are
 * reply buttons at 20 characters, four or more become a list at 24, a body over 1,024 is
 * refused — belong to the channel, not to menus, and a task's choices go out through the
 * same `renderChoices` layer that an entry menu does. Two mocks counting the same limits
 * would drift the first time WhatsApp changed one, and the Menus page would be the one that
 * got updated.
 *
 * THE BUBBLE IS EMPTY ON PURPOSE. A step's instruction is addressed to the assistant, which
 * phrases the question itself in the customer's own language; drawing the instruction as the
 * message would teach an owner that what she typed is what gets sent. `MenuPreview` renders
 * its own "Your assistant's reply appears here." for an empty body, which is exactly true
 * here, so the caption below only has to explain the options.
 */
import { computed } from 'vue';

import MenuPreview from './MenuPreview.vue';
import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { stepPreviewDraft } from '../helpers/task-flow-draft.js';

const props = defineProps({
  /** A step row — see `helpers/task-flow-draft.js`. */
  step: { type: Object, required: true },
  /**
   * For `choose_live`, the labels a real test call produced. Empty until "Try it" has run:
   * a live step with no sample has nothing honest to draw, and inventing three rows would
   * be a preview of a task that does not exist.
   */
  liveLabels: { type: Array, default: () => [] },
  /** How many rows the connection really returned, when that is known. */
  liveTotal: { type: Number, default: 0 },
  /** True when the list was longer than ten and the engine will add "More…". */
  liveMore: { type: Boolean, default: false },
});

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.EDITOR.FLOW.PREVIEW.${name}`;

const draft = computed(() => stepPreviewDraft(props.step, props.liveLabels));

const isLive = computed(() => props.step.type === 'choose_live');
const hasOptions = computed(() => draft.value.options.length > 0);

/**
 * Field fixes G2.3: a question they may pick more than one answer to goes out as a numbered
 * list in the text on every channel, never as buttons (the sidecar's `renderChoices` with
 * `pickMany`), so that is what is drawn. `many` rides on the row's `source`.
 */
const isMany = computed(
  () => props.step.type === 'choose' && props.step.source?.many !== undefined
);
const numbered = computed(() => {
  const digits = new Intl.NumberFormat(toIntlLocale(locale.value));
  return draft.value.options.map((option, at) => ({
    id: option.id,
    line: `${digits.format(at + 1)}. ${option.titleEn || option.titleAr}`,
  }));
});
</script>

<template>
  <div class="flex flex-col gap-2" data-waslo-step-preview>
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ t(key('HEADING')) }}
    </p>

    <template v-if="hasOptions && isMany">
      <ol
        class="flex flex-col gap-1 p-3 mb-0 list-none rounded-xl bg-n-solid-1"
        data-waslo-step-preview-many
      >
        <li
          v-for="option in numbered"
          :key="option.id"
          class="text-sm text-n-slate-12"
          dir="auto"
        >
          {{ option.line }}
        </li>
      </ol>
      <p class="mb-0 text-sm text-n-slate-11" data-waslo-step-preview-many-note>
        {{ t(key('MANY')) }}
      </p>
    </template>
    <MenuPreview v-else-if="hasOptions" :draft="draft" />
    <p
      v-else
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-preview-empty
    >
      {{ isLive ? t(key('LIVE_EMPTY')) : t(key('NO_OPTIONS')) }}
    </p>

    <!-- The "More…" row the engine adds past ten, named here so an owner who tested with
         forty rows is not surprised by a list of ten on a real chat. -->
    <p
      v-if="isLive && liveMore"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-preview-more
    >
      {{ t(key('MORE'), { total: liveTotal }) }}
    </p>
    <!-- The rule this whole screen is arranged around, said once, where the options are. -->
    <p
      v-if="hasOptions"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-step-preview-values
    >
      {{ t(key('VALUES_HIDDEN')) }}
    </p>
  </div>
</template>
